/**
 * Normalisation de la configuration d'une lecon HTML/CSS.
 *
 * Les configurations enregistrees en base proviennent de plusieurs generations
 * du module et peuvent etre partielles, imbriquees differemment ou porter des
 * champs absents. Ce module ramene toutes ces formes a une structure unique
 * pour que les composants n'aient jamais a se prononcer sur leur validite.
 */

import { HtmlCssConfigContent, HtmlCssHelpItem } from './types';
import { CSS_HELP_ITEMS, HTML_HELP_ITEMS } from '../assets/help';
import { serializeWorkspace } from './workspace';

export const DEFAULT_HTML_CODE = '<div>\n  <h1>Hello World</h1>\n</div>';
export const DEFAULT_CSS_CODE = 'h1 {\n  color: blue;\n}';

/** Ne conserve que les entrees d'aide reellement exploitables. */
const sanitizeHelpItems = (items: unknown): HtmlCssHelpItem[] => {
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item): item is HtmlCssHelpItem =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as HtmlCssHelpItem).label === 'string' &&
      typeof (item as HtmlCssHelpItem).code === 'string'
  );
};

/** Ne conserve que les conditions non vides. */
const sanitizeWinConditions = (conditions: unknown): string[] => {
  if (!Array.isArray(conditions)) return [];
  return conditions.filter(
    (condition): condition is string => typeof condition === 'string' && condition.trim() !== ''
  );
};

/** Configuration utilisee lorsqu'une lecon n'en porte aucune. */
export const getDefaultConfigContent = (): HtmlCssConfigContent => ({
  htmlHelpItems: [...HTML_HELP_ITEMS],
  cssHelpItems: [...CSS_HELP_ITEMS],
  // `selectorHelpItems` reste volontairement absent : l'aide aux selecteurs
  // n'est proposee que si l'enseignant l'a explicitement configuree.
  winConditions: [],
  adminWorkspace: serializeWorkspace({ html: DEFAULT_HTML_CODE, css: DEFAULT_CSS_CODE }),
});

/**
 * Ramene une configuration brute a la structure attendue.
 *
 * Une liste d'aide vide est une intention valide de l'enseignant (masquer le
 * bouton) : on ne la remplace donc par les valeurs par defaut que lorsque le
 * champ est absent ou n'est pas un tableau.
 */
export const normalizeConfigContent = (raw: unknown): HtmlCssConfigContent => {
  const defaults = getDefaultConfigContent();

  if (!raw || typeof raw !== 'object') return defaults;

  const source = raw as Partial<HtmlCssConfigContent>;

  return {
    htmlHelpItems: Array.isArray(source.htmlHelpItems)
      ? sanitizeHelpItems(source.htmlHelpItems)
      : defaults.htmlHelpItems,
    cssHelpItems: Array.isArray(source.cssHelpItems)
      ? sanitizeHelpItems(source.cssHelpItems)
      : defaults.cssHelpItems,
    selectorHelpItems: Array.isArray(source.selectorHelpItems)
      ? sanitizeHelpItems(source.selectorHelpItems)
      : undefined,
    winConditions: sanitizeWinConditions(source.winConditions),
    adminWorkspace:
      typeof source.adminWorkspace === 'string' && source.adminWorkspace.trim() !== ''
        ? source.adminWorkspace
        : defaults.adminWorkspace,
  };
};

/**
 * Extrait le contenu de configuration d'une lecon.
 *
 * Le champ `config` d'une lecon est tantot le contenu lui-meme, tantot un objet
 * qui l'enveloppe (`{ lesson_id, config }`). Les deux formes sont acceptees.
 */
export const extractConfigContent = (lessonConfig: unknown): HtmlCssConfigContent => {
  if (!lessonConfig || typeof lessonConfig !== 'object') {
    return getDefaultConfigContent();
  }

  const wrapper = lessonConfig as { config?: unknown };
  const inner = wrapper.config && typeof wrapper.config === 'object' ? wrapper.config : lessonConfig;

  return normalizeConfigContent(inner);
};
